import {Disclosure} from '@github-ui/disclosure'
import {clsx} from 'clsx'
import {use, useCallback, useId, useState} from 'react'
import type {ReactNode, Ref} from 'react'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import {GroupedContext} from './sidebar-context'
import type {ChildrenProps} from './sidebar-context'
import {DisclosurePanel} from './DisclosurePanel'
import rowStyles from './Row.module.css'
import styles from './Accordion.module.css'
import {containsCurrent, sidebarChildKind} from './contains-current'

/** Nested disclosure within a titled Section; use a collapsible Section for top-level disclosure. */
export interface AccordionProps extends ChildrenProps {
  /** Text label for the accordion's disclosure button. */
  title: string
  /** Controlled open state. Pair it with `onOpenChange`. */
  open?: boolean
  /** Initial open state when uncontrolled. Defaults to open when the accordion contains the current page. */
  defaultOpen?: boolean
  /** Called with the next open state when the disclosure button is toggled. */
  onOpenChange?: (open: boolean) => void
  /** One compact control shown before the chevron on hover or keyboard focus. It does not toggle the accordion. */
  action?: ReactNode
  /** Class applied to the outer accordion wrapper, for example to add spacing between accordions. */
  className?: string
  /** Ref to the disclosure button. */
  ref?: Ref<HTMLButtonElement>
}

export const Accordion = Object.assign(
  function SidebarAccordion({
    title,
    open: controlledOpen,
    defaultOpen,
    onOpenChange,
    action,
    className,
    children,
    ref,
  }: AccordionProps) {
    const grouped = use(GroupedContext)
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const hasCurrent = containsCurrent(children)
    const currentDescriptionId = useId()
    const [internalOpen, setInternalOpen] = useState(() => defaultOpen ?? hasCurrent)
    const open = controlledOpen ?? internalOpen
    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (controlledOpen === undefined) setInternalOpen(nextOpen)
        onOpenChange?.(nextOpen)
      },
      [controlledOpen, onOpenChange],
    )
    if (!grouped) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Accordion] Use Sidebar.Section with title, icon, and variant="collapsible" for a top-level disclosure. An Accordion outside a titled Section is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    return (
      <GroupedContext value>
        <Disclosure open={open} onOpenChange={handleOpenChange}>
          <div className={className} data-component="Sidebar.Accordion">
            <div className={clsx(styles.Header, actionStyles.actionRow, actionStyles.disclosureRow)}>
              <Disclosure.Trigger
                render={<button type="button" />}
                ref={ref}
                aria-describedby={!open && hasCurrent ? currentDescriptionId : undefined}
                className={clsx(
                  rowStyles.Row,
                  rowStyles.textLed,
                  styles.Trigger,
                  !open && hasCurrent && rowStyles.current,
                )}
              >
                <span className={rowStyles.Label}>{title}</span>
                {!open && hasCurrent ? (
                  <span id={currentDescriptionId} hidden>
                    Contains the current page
                  </span>
                ) : null}
                {action ? <span aria-hidden className={actionStyles.ActionSpace} /> : null}
                <Disclosure.Chevron className={rowStyles.Chevron} />
              </Disclosure.Trigger>
              {action ? <SidebarAction hover>{action}</SidebarAction> : null}
            </div>
            <DisclosurePanel>
              <div className={styles.AccordionItems}>{children}</div>
            </DisclosurePanel>
          </div>
        </Disclosure>
      </GroupedContext>
    )
  },
  {[sidebarChildKind]: 'container' as const},
)
